// =============================================================================
// SimSense Platform — Session Overview Page
// =============================================================================

import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { formatLapTime, formatLapTimeMs } from "../utils/formatters";
import { api, downloadFile } from "../utils/api";
import { useStore } from "../state/useStore";
import { useAuthStore } from "../state/authStore";
import { useUnits } from "../hooks/useUnits";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import { parseWeather } from "../utils/weatherParser";
import { CoachingModal } from "../components/CoachingModal";
import { TelemetryDetailModal } from "../components/TelemetryDetailModal";
import { getSessionCarFolderHint, installSessionSetupFromLocal, isDesktopSetupInstallAvailable } from "../utils/setupInstaller";

// OT-087: Column definitions for the lap table
type ColumnKey = "trackStatus" | "lap" | "time" | "delta" | "fuelUsed" | "sessionType" | "pit" | "details";
interface ColumnDef { key: ColumnKey; label: string; alwaysVisible?: boolean; defaultVisible: boolean; }
const LAP_COLUMNS: ColumnDef[] = [
  { key: "trackStatus", label: "Track Status", defaultVisible: true },
  { key: "lap", label: "Lap", alwaysVisible: true, defaultVisible: true },
  { key: "time", label: "Time", alwaysVisible: true, defaultVisible: true },
  { key: "delta", label: "Delta", defaultVisible: true },
  { key: "fuelUsed", label: "Fuel Used", defaultVisible: true },
  { key: "sessionType", label: "Session Type", defaultVisible: false },
  { key: "pit", label: "Pit", defaultVisible: false },
  { key: "details", label: "", alwaysVisible: true, defaultVisible: true },
];
const COL_STORAGE_KEY = "osc_lap_columns";
function loadColumnPrefs(): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(COL_STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as ColumnKey[]);
  } catch { /* ignore */ }
  return new Set(LAP_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
}
function saveColumnPrefs(visible: Set<ColumnKey>) {
  localStorage.setItem(COL_STORAGE_KEY, JSON.stringify([...visible]));
}

// --- Setup Helpers [OT-128/157] ---

import { buildSetupSections, renderSetupValue } from "../components/SetupDiffViewer";

export function SessionOverview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { deleteSession } = useStore();
  const user = useAuthStore(s => s.user);
  const units = useUnits();
  const toast = useToast();
  const confirmDialog = useConfirm();
  
  const [session, setSession] = useState<any>(null);
  const [manifest, setManifest] = useState<any>(null);
  const [liteCoaching, setLiteCoaching] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [coachingModal, setCoachingModal] = useState<{ stintIndex: number } | null>(null);
  const [coachingUsage, setCoachingUsage] = useState<{ full_reports_remaining: number | null; full_report_cap: number | null; is_pro: boolean } | null>(null);
  const [telemModal, setTelemModal] = useState<{ globalLap: number; lapNum: number } | null>(null);
  const [visibleCols, setVisibleCols] = useState<Set<ColumnKey>>(loadColumnPrefs);
  const [showColMenu, setShowColMenu] = useState(false);
  const colMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [sData, mData] = await Promise.all([
          api<any>(`/api/sessions/${id}`),
          api<any>(`/api/sessions/${id}/lap-manifest`),
        ]);
        setSession(sData);
        setManifest(mData);
        document.title = `${sData.trackName || "Session"} | SimSense`;

        // Fetch coaching usage for free-tier indicator
        api<any>(`/api/coaching/usage/me`).then(u => setCoachingUsage(u)).catch(() => {});

        // Fetch lite coaching for eligible stints (10+ laps)
        mData.stints.forEach(async (stint: any) => {
          const lapCount = (stint.endLap - stint.startLap) + 1;
          if (lapCount >= 10) {
            try {
              const coaching = await api<any>(`/api/coaching/lite/${id}/${stint.stintIndex}`);
              setLiteCoaching(prev => ({ ...prev, [stint.stintIndex]: coaching }));
            } catch (err) {
              console.error("Failed to fetch lite coaching", err);
            }
          }
        });
      } catch (err) {
        console.error("Failed to fetch session details", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  // OT-087: Close column menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) setShowColMenu(false);
    }
    if (showColMenu) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showColMenu]);

  const toggleColumn = (key: ColumnKey) => {
    const col = LAP_COLUMNS.find(c => c.key === key);
    if (col?.alwaysVisible) return;
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveColumnPrefs(next);
      return next;
    });
  };

  const isColVisible = (key: ColumnKey) => visibleCols.has(key);

  const handleDelete = async () => {
    const ok = await confirmDialog({
      title: "Delete Session",
      message: "Are you sure you want to delete this session? This will remove all associated telemetry, stints, and AI reports.",
      danger: true,
      confirmLabel: "Delete Session"
    });
    if (!ok) return;

    setIsDeleting(true);
    try {
      await deleteSession(id!);
      navigate("/");
    } catch (err) {
      toast.error("Failed to delete session.");
      setIsDeleting(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div className="skeleton" style={{ height: "16px", width: "200px" }} />
      <div className="skeleton skeleton-title" style={{ width: "300px" }} />
      <div className="skeleton skeleton-card" style={{ height: "80px" }} />
      <div className="skeleton skeleton-card" style={{ height: "300px" }} />
    </div>
  );
  if (!session) return <div style={{ padding: "40px", textAlign: "center" }}>Session not found.</div>;

  const isOwner = Boolean(user && session.userId === user.userId);
  const canAccessPremiumCoaching = isOwner;

  function getSkiesLabel(val: any): { label: string, icon: string } {
    // Handle numeric codes from telemetry channels
    const n = parseInt(String(val), 10);
    if (!isNaN(n) && n >= 0 && n <= 4) {
      switch (n) {
        case 0: return { label: "Clear", icon: "☀️" };
        case 1: return { label: "Partly Cloudy", icon: "🌤️" };
        case 2: return { label: "Mostly Cloudy", icon: "🌥️" };
        case 3: return { label: "Overcast", icon: "☁️" };
        case 4: return { label: "Foggy", icon: "🌫️" };
      }
    }
    // Handle string labels from WeekendOptions metadata
    if (typeof val === "string") {
      const lower = val.toLowerCase();
      if (lower.includes("clear")) return { label: "Clear", icon: "☀️" };
      if (lower.includes("partly")) return { label: "Partly Cloudy", icon: "🌤️" };
      if (lower.includes("mostly")) return { label: "Mostly Cloudy", icon: "🌥️" };
      if (lower.includes("overcast")) return { label: "Overcast", icon: "☁️" };
      if (lower.includes("fog")) return { label: "Foggy", icon: "🌫️" };
      if (lower.includes("rain")) return { label: "Rain", icon: "🌧️" };
      if (lower.includes("dynamic")) return { label: "Dynamic", icon: "🌤️" };
      return { label: val, icon: "☁️" };
    }
    return { label: "Unknown", icon: "☁️" };
  }


  function getFlagInfo(flags: string | number | null | undefined): { label: string; color: string; icon: string } | null {
    if (flags == null) return null;
    const n = typeof flags === "string" ? parseInt(flags, 10) : flags;
    if (isNaN(n) || n === 0) return null;
    if (n & 0x0010) return { label: "Red Flag", color: "#ef4444", icon: "🔴" };
    if (n & 0x10000 || n & 0x20000) return { label: "Black Flag", color: "#1f2937", icon: "⚫" };
    if (n & 0x0008 || n & 0x4000 || n & 0x8000 || n & 0x0100) return { label: "Yellow / Caution", color: "#eab308", icon: "🟡" };
    if (n & 0x0001) return { label: "Checkered", color: "#a78bfa", icon: "🏁" };
    if (n & 0x0002) return { label: "White Flag", color: "#e5e7eb", icon: "⚪" };
    return { label: "Green Flag", color: "#22c55e", icon: "🟢" };
  }

  const activeSetup = session.setupJson || session.metadata?.CarSetup;

  return (
    <div>
      {/* Breadcrumbs */}
      <div style={{ marginBottom: "16px", fontSize: "13px", color: "var(--text2)" }}>
        <Link to="/" style={{ color: "var(--accent)", textDecoration: "none" }}>Sessions</Link>
        {" / "}
        <span>{session.trackName}</span>
      </div>

      {/* Header */}
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ marginBottom: "4px", fontSize: "28px" }}>{session.trackName}</h2>
          <div style={{ color: "var(--text2)", fontSize: "15px", marginBottom: "8px" }}>
            {session.driverName && (
              <span style={{ color: "var(--accent)", fontWeight: 600 }}>{session.driverName} — </span>
            )}
            {session.carName} — {new Date(session.sessionDate).toLocaleString()}
          </div>

          {/* Weather Summary — OT-257: Graphical weather bar */}
          {(() => {
            const pw = parseWeather(session.weather);
            if (!pw) return null;
            const { airTempC, trackTempC, windMs, humidity, skyLabel, windDir } = pw;
            const skyInfo = getSkiesLabel(skyLabel);
            const hasAny = skyLabel || airTempC != null || windMs != null;
            if (!hasAny) return null;

            return (
              <div
                style={{
                  marginBottom: "12px",
                  display: "flex",
                  gap: "2px",
                  alignItems: "stretch",
                  width: "fit-content",
                  fontSize: "12px",
                }}
              >
                {skyLabel != null && (
                  <div title="Sky Condition" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", padding: "8px 14px", borderRadius: "8px 0 0 8px", border: "1px solid var(--border)", gap: "2px" }}>
                    <span style={{ fontSize: "20px" }}>{skyInfo.icon}</span>
                    <span style={{ color: "var(--text2)", fontWeight: 600, fontSize: "11px" }}>{skyInfo.label}</span>
                  </div>
                )}
                {airTempC != null && (
                  <div title="Air Temperature" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", padding: "8px 14px", border: "1px solid var(--border)", gap: "2px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 700, color: "var(--text)" }}>{units.formatTemp(airTempC)}</span>
                    <span style={{ color: "var(--text3)", fontSize: "10px", textTransform: "uppercase" }}>Air</span>
                  </div>
                )}
                {trackTempC != null && (
                  <div title="Track Temperature" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", padding: "8px 14px", border: "1px solid var(--border)", gap: "2px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 700, color: "var(--text)" }}>{units.formatTemp(trackTempC)}</span>
                    <span style={{ color: "var(--text3)", fontSize: "10px", textTransform: "uppercase" }}>Track</span>
                  </div>
                )}
                {windMs != null && (
                  <div title="Wind Speed" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", padding: "8px 14px", border: "1px solid var(--border)", gap: "2px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 700, color: "var(--text)" }}>{units.formatSpeed(windMs)}{windDir ? ` ${windDir}` : ""}</span>
                    <span style={{ color: "var(--text3)", fontSize: "10px", textTransform: "uppercase" }}>Wind</span>
                  </div>
                )}
                {humidity != null && (
                  <div title="Humidity" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", padding: "8px 14px", borderRadius: "0 8px 8px 0", border: "1px solid var(--border)", gap: "2px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 700, color: "var(--text)" }}>{Math.round(humidity)}%</span>
                    <span style={{ color: "var(--text3)", fontSize: "10px", textTransform: "uppercase" }}>Humidity</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Setup & Best Laps Summary */}
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            {["Qualify", "Race", "Practice"].map(type => {
              const typeStints = manifest?.stints?.filter((s: any) => s.sessionType?.toLowerCase().includes(type.toLowerCase())) || [];
              if (typeStints.length === 0) return null;

              const rawLaps = manifest?.laps?.filter((l: any) => typeStints.some((s: any) => s.stintIndex === l.stintIndex) && l.isValid && l.lapTime > 0 && l.lapNum > 0) || [];
              // Outlier filter: exclude laps < 50% of median (pit exits, incomplete laps)
              const sortedTimes = rawLaps.map((l: any) => l.lapTime).sort((a: number, b: number) => a - b);
              const median = sortedTimes.length > 0 ? sortedTimes[Math.floor(sortedTimes.length / 2)] : 0;
              const laps = median > 0 ? rawLaps.filter((l: any) => l.lapTime >= median * 0.5) : rawLaps;
              const bestLap = laps.length > 0 ? Math.min(...laps.map((l: any) => l.lapTime)) : null;
              // Collect setup names from stints, fall back to session metadata
              const stintSetups = typeStints.map((s: any) => s.setupName).filter(Boolean);
              const sessionSetup = session.metadata?.SetupName;
              const rawNames = stintSetups.length > 0 ? stintSetups : (sessionSetup ? [sessionSetup] : []);
              const setups: string[] = Array.from(new Set(rawNames.map((n: string) => n.split("/").pop()?.split("\\").pop() || n)));

              return (
                <div key={type} style={{ background: "var(--card)", padding: "12px 16px", borderRadius: "8px", border: "1px solid var(--border)", minWidth: "200px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "var(--accent)", marginBottom: "8px" }}>{type}</div>
                  <div style={{ fontSize: "20px", fontWeight: 700, marginBottom: "4px" }}>{bestLap ? formatLapTime(bestLap) : "—"}</div>
                  <div
                    onClick={() => activeSetup && setShowSetup(true)}
                    style={{
                      fontSize: "12px",
                      color: activeSetup ? "var(--accent)" : "var(--text2)",
                      cursor: activeSetup ? "pointer" : "default",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textDecoration: activeSetup ? "underline" : "none",
                      textDecorationStyle: "dotted",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    title={activeSetup ? "Click to view setup details" : setups.join(", ")}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {setups.length > 0 ? setups[0] : "Default Setup"}
                      {setups.length > 1 && ` (+${setups.length - 1})`}
                    </span>
                    {activeSetup && (
                      <span style={{ fontSize: "8px", background: "var(--accent)", color: "#fff", padding: "1px 4px", borderRadius: "3px", fontWeight: 800 }}>INFO</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button onClick={handleDelete} disabled={isDeleting} style={{ height: "36px", padding: "0 16px", background: "rgba(248, 113, 113, 0.1)", border: "1px solid rgba(248, 113, 113, 0.2)", color: "#f87171", borderRadius: "var(--radius-sm)", fontSize: "13px", fontWeight: 600, cursor: isDeleting ? "not-allowed" : "pointer" }}>
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
          <button onClick={() => void downloadFile(`/api/sessions/${id}/export/csv`, `${session.trackName || "session"}_laps.csv`)} style={{ height: "36px", padding: "0 16px", background: "var(--card)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: "var(--radius-sm)", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
            Export CSV
          </button>
          <Link to={`/session/${id}/compare`} style={{ height: "36px", display: "flex", alignItems: "center", padding: "0 16px", background: "var(--card)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: "var(--radius-sm)", fontSize: "13px", fontWeight: 600, textDecoration: "none" }}>
            Compare Laps
          </Link>
          <Link to={`/session/${id}/run-averages`} style={{ height: "36px", display: "flex", alignItems: "center", padding: "0 16px", background: "var(--card)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: "var(--radius-sm)", fontSize: "13px", fontWeight: 600, textDecoration: "none" }}>
            Run Averages
          </Link>
          <Link to={`/session/${id}/corners`} style={{ height: "36px", display: "flex", alignItems: "center", padding: "0 16px", background: "var(--accent)", color: "#fff", borderRadius: "var(--radius-sm)", fontSize: "13px", fontWeight: 700, textDecoration: "none" }}>
            Corner Analysis
          </Link>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 3fr", gap: "24px" }}>
        {/* Sidebar: Stints */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <h3 style={{ fontSize: "0.75rem", textTransform: "uppercase", color: "var(--text2)" }}>Runs</h3>
          {manifest?.stints?.map((stint: any) => (
            <div key={stint.stintIndex} style={{ display: "block", padding: "16px", background: "var(--card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", color: "inherit" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <div style={{ fontWeight: 600, fontSize: "14px" }}>Run {stint.stintIndex + 1}</div>
                <button onClick={() => void downloadFile(`/api/sessions/${id}/stints/${stint.stintIndex}/export/xlsx`, `run_${stint.stintIndex + 1}.xlsx`)} style={{ background: "none", border: "none", color: "var(--text2)", cursor: "pointer", fontSize: "11px" }}>📥 XLSX</button>
              </div>
              <div style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "4px" }}>Laps {stint.startLap} – {stint.endLap}</div>
              {stint.isCommercial && (
                <div title={stint.commercialBrand ? `Official setup by ${stint.commercialBrand}. Commercial setups are protected from public sharing.` : "This is a commercial setup. Sharing is restricted."} style={{ fontSize: "10px", background: "rgba(245, 158, 11, 0.12)", color: "#fbbf24", padding: "3px 8px", borderRadius: "4px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "4px", marginBottom: "12px", border: "1px solid rgba(245, 158, 11, 0.25)" }}>
                  <span style={{ fontSize: "10px" }}>&#x2606;</span> {stint.commercialBrand ? `Official ${stint.commercialBrand} Setup` : "Commercial Setup"}
                </div>
              )}
              {stint.summary && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "12px" }}>
                  <div style={{ fontSize: "11px" }}>
                    <div style={{ color: "var(--text2)" }}>Avg Lap</div>
                    <div style={{ fontWeight: 600 }}>{formatLapTimeMs(stint.summary.avgLapMs)}</div>
                  </div>
                  <div style={{ fontSize: "11px" }}>
                    <div style={{ color: "var(--text2)" }}>Consistency</div>
                    <div style={{ fontWeight: 600 }}>±{units.formatDelta(stint.summary.stddevMs / 1000).replace('s', '')}s</div>
                  </div>
                </div>
              )}
              {stint.tireData && hasTireValues(stint.tireData) && (
                <TireVisualPanel tireData={stint.tireData} units={units} />
              )}
              {canAccessPremiumCoaching && (
                <div>
                  <button
                    onClick={() => setCoachingModal({ stintIndex: stint.stintIndex })}
                    disabled={!!(coachingUsage && !coachingUsage.is_pro && coachingUsage.full_reports_remaining === 0)}
                    style={{
                      width: "100%", textAlign: "center", padding: "8px",
                      background: coachingUsage && !coachingUsage.is_pro && coachingUsage.full_reports_remaining === 0 ? "var(--border)" : "var(--accent)",
                      color: "#fff", borderRadius: "4px", fontSize: "11px", fontWeight: 600, border: "none",
                      cursor: coachingUsage && !coachingUsage.is_pro && coachingUsage.full_reports_remaining === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    {coachingUsage && !coachingUsage.is_pro && coachingUsage.full_reports_remaining === 0 ? "REPORT LIMIT REACHED" : "ADVANCED COACHING REPORT"}
                  </button>
                  {coachingUsage && !coachingUsage.is_pro && coachingUsage.full_report_cap != null && (
                    <div style={{ marginTop: "6px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: "var(--text3)", marginBottom: "3px" }}>
                        <span>{coachingUsage.full_reports_remaining === 0 ? "Upgrade for unlimited" : `${coachingUsage.full_reports_remaining} of ${coachingUsage.full_report_cap} remaining`}</span>
                        {coachingUsage.full_reports_remaining === 0 && <a href="/pricing" style={{ color: "var(--accent)", fontSize: "10px" }}>Go Pro</a>}
                      </div>
                      <div style={{ height: "3px", borderRadius: "2px", background: "var(--border)" }}>
                        <div style={{
                          height: "100%", borderRadius: "2px",
                          width: `${((coachingUsage.full_report_cap - (coachingUsage.full_reports_remaining ?? 0)) / coachingUsage.full_report_cap) * 100}%`,
                          background: coachingUsage.full_reports_remaining === 0 ? "var(--bad)" : "var(--accent)",
                          transition: "width 0.3s ease",
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isOwner && liteCoaching[stint.stintIndex] && (
                <div style={{ marginTop: "16px", padding: "12px", background: "rgba(255,255,255,0.03)", borderRadius: "4px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", marginBottom: "8px", textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
                    <span>Coach Observations</span>
                    <span style={{ opacity: 0.6 }}>FREE</span>
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {(liteCoaching[stint.stintIndex].observations || [liteCoaching[stint.stintIndex].summary]).map((obs: string, i: number) => (
                      <li key={i} style={{ fontSize: "12px", lineHeight: "1.4", color: "var(--text)", display: "flex", gap: "6px" }}>
                        <span style={{ color: "var(--accent)" }}>•</span>{obs}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main Content Area — Laps */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px", position: "relative", borderBottom: "1px solid var(--border)" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text2)" }}>Laps ({manifest?.laps?.length || 0})</div>
                <div ref={colMenuRef} style={{ position: "relative" }}>
                  <button onClick={() => setShowColMenu(v => !v)} style={{ background: "none", border: "1px solid var(--border)", color: "var(--text2)", padding: "4px 10px", borderRadius: "4px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ fontSize: "13px" }}>&#9776;</span> Columns
                  </button>
                  {showColMenu && (
                    <div style={{ position: "absolute", right: 0, top: "100%", marginTop: "4px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "8px", padding: "8px 0", zIndex: 50, minWidth: "180px", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
                      {LAP_COLUMNS.filter(c => !c.alwaysVisible && c.key !== "details").map(col => (
                        <label key={col.key} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 14px", fontSize: "13px", cursor: "pointer", color: "var(--text)" }}>
                          <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => toggleColumn(col.key)} style={{ width: "14px", height: "14px" }} />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", background: "rgba(255,255,255,0.02)" }}>
                    {isColVisible("trackStatus") && <th style={{ padding: "12px 16px", width: "48px", textAlign: "center" }}>Track Status</th>}
                    <th style={{ padding: "12px 16px" }}>Lap</th>
                    <th style={{ padding: "12px 16px" }}>Time</th>
                    {isColVisible("delta") && <th style={{ padding: "12px 16px" }}>Delta</th>}
                    {isColVisible("fuelUsed") && <th style={{ padding: "12px 16px" }}>Fuel Used</th>}
                    {isColVisible("sessionType") && <th style={{ padding: "12px 16px" }}>Type</th>}
                    {isColVisible("pit") && <th style={{ padding: "12px 16px" }}>Pit</th>}
                    <th style={{ padding: "12px 16px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const laps = manifest?.laps || [];
                    const cleanTimes = laps.filter((l: any) => l.lapTime > 0 && !l.pitIn && !l.pitOut && l.isValid).map((l: any) => l.lapTime).sort((a: number, b: number) => a - b);
                    const median = cleanTimes.length > 0 ? cleanTimes[Math.floor(cleanTimes.length / 2)] : 0;
                    const bestTime = cleanTimes.length > 0 ? Math.min(...cleanTimes.filter((t: number) => t > median * 0.5)) : 0;
                    return laps.map((lap: any) => {
                      const delta = lap.lapTime > 0 && bestTime > 0 && !lap.pitIn && !lap.pitOut ? lap.lapTime - bestTime : null;
                      return (
                        <tr key={lap.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          {isColVisible("trackStatus") && (
                            <td style={{ padding: "12px 8px", textAlign: "center", width: "48px" }}>
                              {(() => {
                                const flag = getFlagInfo(lap.sessionFlags);
                                if (!flag) return null;
                                return <div title={flag.label} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "6px", backgroundColor: `${flag.color}20`, border: `1px solid ${flag.color}40`, color: flag.color, fontSize: "14px" }}>{flag.icon}</div>;
                              })()}
                            </td>
                          )}
                          <td style={{ padding: "12px 16px" }}>{lap.lapNum}</td>
                          <td style={{ padding: "12px 16px", fontVariantNumeric: "tabular-nums" }} title={lap.lapTimeSource === "tick_computed" ? "Estimated from track position (LastLapTime channel unavailable)" : undefined}>
                            {lap.lapTimeSource === "tick_computed" && <span style={{ color: "var(--warn)", fontSize: "10px", marginRight: "2px" }}>~</span>}
                            {formatLapTime(lap.lapTime)}
                          </td>
                          {isColVisible("delta") && (
                            <td style={{ padding: "12px 16px", fontVariantNumeric: "tabular-nums", color: delta === 0 ? "var(--good)" : delta != null ? "var(--text2)" : "var(--text3)" }}>
                              {delta === 0 ? "Best" : delta != null ? units.formatDelta(delta) : "—"}
                            </td>
                          )}
                          {isColVisible("fuelUsed") && (
                            <td style={{ padding: "12px 16px", fontVariantNumeric: "tabular-nums", color: "var(--text2)" }}>
                              {lap.fuelUsed != null && lap.fuelUsed > 0 ? units.formatFuel(lap.fuelUsed) : <span style={{ opacity: 0.4, fontSize: "11px" }}>N/A</span>}
                            </td>
                          )}
                          {isColVisible("sessionType") && <td style={{ padding: "12px 16px", fontSize: "11px", color: "var(--text2)" }}>{lap.sessionType || "—"}</td>}
                          {isColVisible("pit") && (
                            <td style={{ padding: "12px 16px", textAlign: "center" }}>
                              {lap.pitIn && <span style={{ fontSize: "11px", background: "rgba(234,179,8,0.15)", color: "#eab308", padding: "2px 6px", borderRadius: "3px", marginRight: "4px" }}>IN</span>}
                              {lap.pitOut && <span style={{ fontSize: "11px", background: "rgba(34,197,94,0.15)", color: "#22c55e", padding: "2px 6px", borderRadius: "3px" }}>OUT</span>}
                            </td>
                          )}
                          <td style={{ padding: "12px 16px", textAlign: "right" }}><Link to={`/session/${id}/compare?laps=${lap.globalLap}`} style={{ display: "inline-block", background: "none", border: "1px solid var(--border)", color: "var(--text)", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: "pointer", textDecoration: "none" }}>Details</Link></td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
          </div>
        </div>
      </div>

      {showSetup && <SetupModal setup={activeSetup} setupName={(() => {
        const stintSetups = (manifest?.stints || []).flatMap((s: any) => s.setupName ? [s.setupName] : []);
        const sessionSetup = session.metadata?.SetupName;
        const rawNames = stintSetups.length > 0 ? stintSetups : (sessionSetup ? [sessionSetup] : []);
        const names: string[] = Array.from(new Set(rawNames.map((n: string) => n.split("/").pop()?.split("\\").pop() || n)));
        return names.length > 0 ? names[0] : null;
      })()} rawSetupName={(() => {
        const stintSetups = (manifest?.stints || []).flatMap((s: any) => s.setupName ? [s.setupName] : []);
        const sessionSetup = session.metadata?.SetupName;
        const rawNames = stintSetups.length > 0 ? stintSetups : (sessionSetup ? [sessionSetup] : []);
        return rawNames.length > 0 ? rawNames[0] : null;
      })()} sessionType={(() => {
        const types = (manifest?.stints || []).map((s: any) => s.sessionType).filter(Boolean);
        if (types.length === 0) return null;
        const unique = Array.from(new Set(types.map((t: string) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())));
        return unique.join(" / ");
      })()} carName={session.carName} sessionMetadata={session.metadata} isDesktop={isDesktopSetupInstallAvailable()} onClose={() => setShowSetup(false)} />}

      {coachingModal && (
        <CoachingModal
          sessionId={id!}
          stintIndex={coachingModal.stintIndex}
          session={session}
          onClose={() => setCoachingModal(null)}
        />
      )}
      {telemModal && manifest && (
        <TelemetryDetailModal
          sessionId={id!}
          globalLap={telemModal.globalLap}
          lapNum={telemModal.lapNum}
          trackName={session.trackName}
          carName={session.carName}
          onClose={() => setTelemModal(null)}
          onNavigate={(gl) => {
            const lap = manifest.laps.find((l: any) => l.globalLap === gl);
            if (lap) setTelemModal({ globalLap: gl, lapNum: lap.lapNum });
          }}
          availableLaps={manifest.laps.filter((l: any) => l.lapNum > 0).map((l: any) => ({ globalLap: l.globalLap, lapNum: l.lapNum }))}
        />
      )}
    </div>
  );
}

function SetupModal({
  setup,
  setupName,
  rawSetupName,
  sessionType,
  carName,
  sessionMetadata,
  isDesktop,
  onClose,
}: {
  setup: any,
  setupName?: string | null,
  rawSetupName?: string | null,
  sessionType?: string | null,
  carName?: string,
  sessionMetadata?: any,
  isDesktop: boolean,
  onClose: () => void
}) {
  const units = useUnits();
  const toast = useToast();
  const [isInstalling, setIsInstalling] = useState(false);
  if (!setup) return null;

  const sections = buildSetupSections(setup);

  const isAttachKey = (flatKey: string) => {
    const lk = flatKey.toLowerCase();
    return lk.includes("attach") || lk.includes("locked") || lk.includes("enabled");
  };
  const isAttachVal = (flatKey: string, val: any) =>
    isAttachKey(flatKey) && (val === 0 || val === 1 || val === "0" || val === "1");

  const renderAttach = (val: any) => {
    const checked = Number(val) === 1;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: "18px", height: "18px",
        border: `2px solid ${checked ? "var(--good)" : "var(--text3)"}`,
        borderRadius: "4px",
        background: checked ? "var(--good)" : "transparent",
        color: "#fff", fontSize: "12px", fontWeight: 700,
        lineHeight: 1,
      }}>
        {checked ? "✓" : ""}
      </span>
    );
  };

  const canInstall = isDesktop && Boolean(rawSetupName?.trim());

  const handleInstall = async () => {
    if (!canInstall || !rawSetupName) return;
    setIsInstalling(true);
    try {
      const result = await installSessionSetupFromLocal({
        carName,
        rawSetupName,
        carFolderHint: getSessionCarFolderHint(sessionMetadata),
      });
      const destinationCount = result.destinationDirs.length;
      toast.success(
        destinationCount > 1
          ? `Installed .sto into ${destinationCount} iRacing setup folders.`
          : "Installed .sto into your SimSense iRacing setup folder."
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to install the .sto file.");
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg2)", width: "100%", maxWidth: "900px", maxHeight: "90vh", borderRadius: "12px", border: "1px solid var(--border)", padding: "24px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "1px solid var(--border)", paddingBottom: "16px" }}>
          <div>
            <h3 style={{ fontSize: "20px", marginBottom: "4px" }}>{sessionType || "Setup"}</h3>
            <div style={{ fontSize: "12px", color: "var(--text2)" }}>{setupName || carName || "Default Setup"}</div>
          </div>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <button
              onClick={() => void handleInstall()}
              disabled={!canInstall || isInstalling}
              title={!isDesktop
                ? "This action is available in the desktop app."
                : !rawSetupName
                  ? "No .sto source name was available for this session."
                  : "Copy the matching .sto into your SimSense iRacing setup folder."}
              style={{
                padding: "8px 16px",
                background: canInstall && !isInstalling ? "var(--card)" : "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)",
                color: canInstall && !isInstalling ? "var(--text)" : "var(--text3)",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: canInstall && !isInstalling ? "pointer" : "not-allowed"
              }}
            >
              {isInstalling ? "Installing..." : isDesktop ? "Install .sto" : "Install .sto (Desktop Only)"}
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text2)", cursor: "pointer", fontSize: "24px" }}>x</button>
          </div>
        </div>

        <div style={{ overflowY: "auto", paddingRight: "10px", display: "flex", flexDirection: "column", gap: "20px" }}>
          {sections.map(section => (
            <div key={section.name}>
              <h4 style={{ fontSize: "12px", textTransform: "uppercase", color: "var(--accent)", marginBottom: "12px", borderBottom: "1px solid var(--border)", paddingBottom: "4px", letterSpacing: "0.5px" }}>
                {section.name}
              </h4>
              {section.pairs.map((pair, pi) => (
                <div key={pair.label || `pair-${pi}`} style={{ marginBottom: "12px" }}>
                  {pair.label && (
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", marginBottom: "6px" }}>{pair.label}</div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: "6px", padding: "10px" }}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", marginBottom: "6px", textAlign: "center" }}>{pair.left.label}</div>
                      {pair.left.items.map(item => (
                        <div key={item.flatKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "2px 0" }}>
                          <span style={{ color: "var(--text2)" }}>{item.displayKey}</span>
                          {isAttachVal(item.flatKey, item.value)
                            ? renderAttach(item.value)
                            : <span style={{ fontWeight: 600, color: "#fff" }}>{renderSetupValue(item.flatKey, item.value, units)}</span>}
                        </div>
                      ))}
                    </div>
                    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: "6px", padding: "10px" }}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent)", marginBottom: "6px", textAlign: "center" }}>{pair.right.label}</div>
                      {pair.right.items.map(item => (
                        <div key={item.flatKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "2px 0" }}>
                          <span style={{ color: "var(--text2)" }}>{item.displayKey}</span>
                          {isAttachVal(item.flatKey, item.value)
                            ? renderAttach(item.value)
                            : <span style={{ fontWeight: 600, color: "#fff" }}>{renderSetupValue(item.flatKey, item.value, units)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {section.shared.length > 0 && (() => {
                const isCentered = section.name.toLowerCase().includes("chassis") || section.name === "Rear Settings";
                return (
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    ...(isCentered ? {
                      maxWidth: "50%",
                      margin: "12px auto 0",
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: "6px",
                      padding: "10px",
                    } : {}),
                  }}>
                    {section.shared.map(item => (
                      <div key={item.flatKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "2px 0" }}>
                        <span style={{ color: "var(--text2)" }}>{item.displayKey}</span>
                        {isAttachVal(item.flatKey, item.value)
                          ? renderAttach(item.value)
                          : <span style={{ fontWeight: 600, color: "#fff" }}>{renderSetupValue(item.flatKey, item.value, units)}</span>}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// OT-190: Only show tire panel when at least one tire has real data
function hasTireValues(tireData: any): boolean {
  for (const corner of ["LF", "RF", "LR", "RR"]) {
    const d = tireData[corner];
    if (!d) continue;
    if (d.hotPressurePsi || d.tempF?.middle || d.wear?.min) return true;
  }
  return false;
}

// OT-250: Temperature → color for tire heat map
// Green < 250F, orange 250-280F, red > 280F. No blue.
function tireTempColor(tempF: number | null | undefined): string {
  if (!tempF) return "rgba(255,255,255,0.06)";
  if (tempF < 180) return "#15803d";  // cool dark green
  if (tempF < 210) return "#22c55e";  // optimal green
  if (tempF < 230) return "#4ade80";  // warm light green
  if (tempF < 250) return "#86efac";  // upper green
  if (tempF < 265) return "#f97316";  // orange
  if (tempF < 280) return "#ea580c";  // dark orange
  return "#dc2626";                    // red (too hot)
}


function TireShape({ data, label, isRight, units }: { data: any, label: string, isRight: boolean, units: any }) {
  const tempO = data?.tempF?.outside;
  const tempM = data?.tempF?.middle;
  const tempI = data?.tempF?.inside;
  const pressure = data?.hotPressurePsi;
  const wearO = data?.wear?.outside;
  const wearM = data?.wear?.middle;
  const wearI = data?.wear?.inside;

  // Left tires: O|M|I left-to-right. Right tires: I|M|O left-to-right.
  const zones = isRight
    ? [{ t: tempI, w: wearI, l: "I" }, { t: tempM, w: wearM, l: "M" }, { t: tempO, w: wearO, l: "O" }]
    : [{ t: tempO, w: wearO, l: "O" }, { t: tempM, w: wearM, l: "M" }, { t: tempI, w: wearI, l: "I" }];

  // Tire data is stored in °F — convert to °C for units.formatTemp() which expects Celsius input
  const fmtTemp = (f: number | null | undefined) => {
    if (!f) return "—";
    return units.formatTemp((f - 32) * 5 / 9);
  };

  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* Corner label */}
      <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text2)", marginBottom: "2px" }}>{label}</div>
      {/* O/M/I labels above tire */}
      <div style={{ display: "flex", width: "100%", justifyContent: "space-around", marginBottom: "2px" }}>
        {zones.map((z, i) => (
          <span key={i} style={{ fontSize: "10px", fontWeight: 600, color: "var(--text3)", textAlign: "center", flex: 1 }}>{z.l}</span>
        ))}
      </div>
      {/* Tire shape — vertical (tall), 3 colored zone strips, rounded top like tread */}
      <div style={{
        display: "flex", width: "100%", height: "56px",
        borderRadius: "8px 8px 4px 4px", overflow: "hidden",
        border: "2px solid rgba(255,255,255,0.12)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.4), inset 0 2px 4px rgba(0,0,0,0.2)",
      }}>
        {zones.map((z, i) => (
          <div key={i} style={{
            flex: 1, background: tireTempColor(z.t),
            borderRight: i < 2 ? "1px solid rgba(0,0,0,0.3)" : "none",
          }} />
        ))}
      </div>
      {/* Temp values below tire — one per zone */}
      <div style={{ display: "flex", width: "100%", marginTop: "4px" }}>
        {zones.map((z, i) => (
          <span key={i} style={{ flex: 1, textAlign: "center", fontSize: "14px", fontWeight: 700, color: "var(--text)" }}>
            {fmtTemp(z.t)}
          </span>
        ))}
      </div>
      {/* Wear values below temps */}
      <div style={{ display: "flex", width: "100%", marginTop: "2px" }}>
        {zones.map((z, i) => (
          <span key={i} style={{ flex: 1, textAlign: "center", fontSize: "12px", fontWeight: 500, color: "var(--text3)" }}>
            {z.w != null ? `${z.w.toFixed(0)}%` : "—"}
          </span>
        ))}
      </div>
      {/* Pressure centered */}
      <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text2)", marginTop: "3px" }}>
        {pressure ? units.formatPressure(pressure * 6.89476) : "—"}
      </div>
    </div>
  );
}

function TireVisualPanel({ tireData, units }: { tireData: any, units: any }) {
  // OT-203: Confidence badge
  const confidence = tireData?._confidence || "full";
  const badgeColor = confidence === "full" ? "var(--good)" : confidence === "partial" ? "var(--warn)" : "var(--text3)";
  const badgeLabel = confidence === "full" ? "Full Data" : confidence === "partial" ? "Estimated" : "No Data";
  return (
    <div style={{ marginBottom: "16px", background: "rgba(0,0,0,0.2)", padding: "14px 12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
        <span style={{ fontSize: "9px", fontWeight: 700, textTransform: "uppercase", color: "var(--text3)", letterSpacing: "0.5px" }}>Tires (at end of run)</span>
        {confidence !== "full" && <span style={{ fontSize: "8px", fontWeight: 700, padding: "1px 5px", borderRadius: "3px", background: `${badgeColor}22`, color: badgeColor, textTransform: "uppercase" }}>{badgeLabel}</span>}
      </div>
      {/* Front row */}
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "12px" }}>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.LF} label="Left" isRight={false} units={units} />
        </div>
        <div style={{ width: "40px", textAlign: "center", paddingTop: "14px" }}>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "var(--text3)" }}>Front</span>
        </div>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.RF} label="Right" isRight={true} units={units} />
        </div>
      </div>
      {/* Rear row */}
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.LR} label="" isRight={false} units={units} />
        </div>
        <div style={{ width: "40px", textAlign: "center", paddingTop: "14px" }}>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "var(--text3)" }}>Rear</span>
        </div>
        <div style={{ flex: 1 }}>
          <TireShape data={tireData.RR} label="" isRight={true} units={units} />
        </div>
      </div>
    </div>
  );
}
